/* /register?token=&email= — Directeur-account aanmaken voor nieuwe KDV.

   Vereist een geldige magic-link token uit de welkomstmail. */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function RegisterPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;
    const t = params.get("token") || "";
    const e = params.get("email") || "";
    setToken(t);
    setEmail(e);
    if (!t) { setTokenValid(false); return; }
    // Optioneel: valideer token via redeem — idempotency-veilig binnen 5min
    api.post("/onboarding/magic-link/redeem", { token: t })
      .then(r => { setTokenValid(true); if (r.data.email) setEmail(r.data.email); })
      .catch(() => setTokenValid(false));
  }, [params]);

  const strength = (() => {
    if (pw.length < 8) return { label: "Te kort", cls: "text-red-600" };
    if (pw.length < 12 || !/\d/.test(pw)) return { label: "Redelijk", cls: "text-amber-600" };
    return { label: "Sterk", cls: "text-emerald-600" };
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Ongeldige link"); return; }
    if (pw.length < 8) { toast.error("Wachtwoord minimaal 8 tekens"); return; }
    if (pw !== pw2) { toast.error("Wachtwoorden komen niet overeen"); return; }
    if (name.trim().length < 2) { toast.error("Vul je naam in"); return; }
    setBusy(true);
    try {
      await api.post("/onboarding/register-owner", { token, password: pw, name: name.trim() });
      toast.success("Account aangemaakt — welkom bij Webster!");
      // Backend zet cookie; nieuwe directeurs naar de setup-wizard sturen zodat ze KDV kunnen opzetten.
      window.location.href = "/app/onboarding-wizard";
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  if (tokenValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-neutral-50 to-sky-50">
        <Card className="max-w-md w-full p-8 bg-white shadow-lg text-center" data-testid="register-invalid">
          <div className="text-lg font-semibold text-red-800 mb-2">Uitnodiging ongeldig</div>
          <p className="text-sm text-neutral-600 mb-4">De registratielink is verlopen of al gebruikt. Vraag de platformbeheerder om een nieuwe uitnodiging.</p>
          <Button onClick={() => navigate("/login")}>Naar inloggen</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-neutral-50 to-emerald-50">
      <Card className="max-w-md w-full p-8 bg-white shadow-lg" data-testid="register-card">
        <div className="text-center mb-5">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <UserPlus className="h-6 w-6 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-semibold text-neutral-900 mb-1">Maak je directeur-account</h1>
          <p className="text-sm text-neutral-600">Kies een sterk wachtwoord om te starten met Webster.</p>
        </div>

        {tokenValid === null && (
          <div className="text-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-neutral-400 mx-auto mb-2" />
            <div className="text-xs text-neutral-500">Uitnodiging valideren…</div>
          </div>
        )}

        {tokenValid && (
          <form onSubmit={submit} className="space-y-3" data-testid="register-form">
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" value={email} disabled className="bg-neutral-50" data-testid="register-email" />
              <div className="text-[10px] text-neutral-500 mt-0.5">Gekoppeld aan je uitnodiging</div>
            </div>
            <div>
              <Label htmlFor="name">Volledige naam</Label>
              <Input id="name" value={name} onChange={e => setName(e.target.value)}
                     placeholder="Bijv. Sanne Jansen" data-testid="register-name" autoFocus />
            </div>
            <div>
              <Label htmlFor="pw">Wachtwoord</Label>
              <div className="relative">
                <Input id="pw" type={showPw ? "text" : "password"} value={pw}
                       onChange={e => setPw(e.target.value)} placeholder="min. 8 tekens"
                       className="pr-10" data-testid="register-password" />
                <button type="button" onClick={() => setShowPw(s => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-600">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {pw && <div className={`text-[11px] mt-0.5 ${strength.cls}`}>Sterkte: {strength.label}</div>}
            </div>
            <div>
              <Label htmlFor="pw2">Bevestig wachtwoord</Label>
              <Input id="pw2" type={showPw ? "text" : "password"} value={pw2}
                     onChange={e => setPw2(e.target.value)} placeholder="Nogmaals"
                     data-testid="register-password-confirm" />
              {pw2 && pw !== pw2 && <div className="text-[11px] text-red-600 mt-0.5">Komen niet overeen</div>}
              {pw2 && pw === pw2 && pw.length >= 8 && <div className="text-[11px] text-emerald-600 mt-0.5 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />OK</div>}
            </div>
            <Button type="submit" disabled={busy} className="w-full text-white h-11" style={{ background: "var(--sage)" }} data-testid="register-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              {busy ? "Account aanmaken…" : "Account aanmaken en inloggen"}
            </Button>
            <div className="text-center pt-2">
              <button type="button" onClick={() => navigate("/login")} className="text-xs text-neutral-500 hover:text-neutral-700 underline">
                Heb je al een account? Log in
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
